/**
 * ข้อความและตัวกรองแถว ledger — mirror API credit-transaction-display.ts (ส่วน label/filter)
 */

import { toAmountDisplay, normalizeReservePercent } from "./creditFormulas";

export const formatCreditDisplayAmount = (amountDisplay: number): string =>
  Math.round(Number(amountDisplay ?? 0)).toLocaleString("th-TH");

export const CREDIT_DEBT_LABEL_SUFFIX = "(เครดิตค้างชำระ)";

export const withCreditDebtSuffix = (base: string, show: boolean): string => {
  if (!show) return base;
  return `${base} ${CREDIT_DEBT_LABEL_SUFFIX}`;
};

export const buildCodFailAdditionalDeductLabel = (
  additionalDisplay: number,
  availableAfterRaw?: number | null,
): string => {
  const x = formatCreditDisplayAmount(additionalDisplay);
  const base = `หักเพิ่ม ${x} เครดิต เนื่องจากส่งพัสดุไม่สำเร็จ`;
  return withCreditDebtSuffix(base, Number(availableAfterRaw ?? 0) < 0);
};

export const formatCreditBalanceAfterDisplay = (
  availableAfterRaw: number | null | undefined,
  multiplier = 2,
): string => {
  const raw = Number(availableAfterRaw ?? 0);
  if (raw < 0) {
    return `-${formatCreditDisplayAmount(toAmountDisplay(-raw, multiplier))}`;
  }
  return formatCreditDisplayAmount(toAmountDisplay(raw, multiplier));
};

const ORDER = "ORDER";
const PARCEL = "PARCEL";

export type CreditTransactionRow = {
  type: string;
  system_type?: string | null;
  note?: string | null;
  display_label_th?: string | null;
  amount?: number | null;
  amount_display?: number | null;
  available_after?: number | null;
  /** % ที่ใช้จริงตอนบันทึกแถวนี้ (COD RESERVE) — ใช้แทน hardcode 50% (เฉพาะ fallback เมื่อ API ไม่ส่ง display_label_th) */
  reserve_percent_used?: number | null;
};

const isCodFailOrderDeduct = (type: string, systemType: string, note: string): boolean => {
  if (type !== "DEDUCT" || systemType !== ORDER) return false;
  return (
    note.includes("เก็บเงิน COD ไม่ได้") ||
    note.includes("backfill: เก็บเงิน COD ไม่ได้") ||
    note.includes("cod:fail_deduct") ||
    note.includes("fail")
  );
};

export const getCreditTransactionDisplayLabel = (row: CreditTransactionRow): string => {
  if (row.display_label_th) return row.display_label_th;

  const type = String(row.type ?? "").toUpperCase();
  const systemType = String(row.system_type ?? "").toUpperCase();
  const note = String(row.note ?? "");

  if (type === "RESERVE" && systemType === ORDER) {
    const percentDisplay = Math.round(normalizeReservePercent(row.reserve_percent_used) * 100);
    return withCreditDebtSuffix(`หักค่าประกันขนส่ง COD ${percentDisplay}%`, Number(row.available_after ?? 0) < 0);
  }

  if (type === "RELEASE" && systemType === ORDER) {
    if (note.includes("เก็บเงินสำเร็จ")) {
      return "คืนค่าประกัน COD เนื่องจากส่งพัสดุสำเร็จ";
    }
    if (
      note.includes("ยกเลิก") ||
      note.includes("refunded") ||
      note.includes("cancel") ||
      note.includes("ก่อนมีเลขพัสดุ")
    ) {
      return "คืนค่าประกัน COD เนื่องจากยกเลิกคำสั่งซื้อ";
    }
    if (
      note.includes("เก็บเงินไม่ได้") ||
      note.includes("fail") ||
      note.includes("ปิดรายการ") ||
      note.includes("write-off")
    ) {
      return "คืนค่าประกันขนส่ง COD — ปิดรายการ";
    }
    return "คืนค่าประกันขนส่ง COD";
  }

  if (isCodFailOrderDeduct(type, systemType, note)) {
    const additional =
      row.amount_display != null && Number(row.amount_display) > 0
        ? Math.round(Number(row.amount_display))
        : row.amount != null
          ? Math.round(toAmountDisplay(Number(row.amount)))
          : 0;
    if (additional > 0) {
      return buildCodFailAdditionalDeductLabel(additional, row.available_after);
    }
    return withCreditDebtSuffix("หักเพิ่มเครดิต เนื่องจากส่งพัสดุไม่สำเร็จ", Number(row.available_after ?? 0) < 0);
  }

  if (type === "DEBT" && !note.includes("cod_reserve_shortfall")) {
    const additional =
      row.amount_display != null && Number(row.amount_display) > 0
        ? Math.round(Number(row.amount_display))
        : row.amount != null
          ? Math.round(toAmountDisplay(Number(row.amount)))
          : 0;
    if (additional > 0) {
      return withCreditDebtSuffix(
        `หักเพิ่ม ${formatCreditDisplayAmount(additional)} เครดิต เนื่องจากส่งพัสดุไม่สำเร็จ`,
        true,
      );
    }
    return withCreditDebtSuffix("หักเพิ่มเครดิต เนื่องจากส่งพัสดุไม่สำเร็จ", true);
  }

  if (type === "DEBT_SETTLEMENT") return "หักเครดิตค้างชำระ";

  if (systemType === PARCEL) {
    switch (type) {
      case "DEDUCT":
        return "หัก — ส่งพัสดุ";
      case "REFUND":
        return "คืน — ส่งพัสดุ";
      case "TOPUP":
        return "เติม — ส่งพัสดุ";
      case "EXPIRED":
        return "หมดอายุ — ส่งพัสดุ";
      default:
        break;
    }
  }

  switch (type) {
    case "TOPUP":
      return "เติมเครดิต";
    case "DEDUCT":
      return "หักเครดิต";
    case "REFUND":
      return "คืนเครดิต";
    case "EXPIRED":
      return "เครดิตหมดอายุ";
    case "MANUAL":
      return "ปรับเครดิต (มือ)";
    case "RESERVE":
      return "จองโควต้า";
    case "RELEASE":
      return "ปลดโควต้า";
    default:
      return type || "—";
  }
};

export type CreditTransactionAffects = "quota" | "balance" | "both" | "none";

export const getCreditTransactionAffects = (row: CreditTransactionRow): CreditTransactionAffects => {
  const type = String(row.type ?? "").toUpperCase();
  const systemType = String(row.system_type ?? "").toUpperCase();

  if (systemType === ORDER) {
    if (type === "RESERVE" || type === "RELEASE") return "both";
    if (type === "DEDUCT") return "balance";
  }

  if (
    type === "TOPUP" ||
    type === "DEDUCT" ||
    type === "REFUND" ||
    type === "EXPIRED" ||
    type === "MANUAL" ||
    type === "DEBT" ||
    type === "DEBT_SETTLEMENT"
  ) {
    return "balance";
  }

  return "none";
};

export const isCodReserveDebtRow = (row: {
  type?: string | null;
  system_type?: string | null;
  note?: string | null;
}): boolean => {
  const type = String(row.type ?? "").toUpperCase();
  const systemType = String(row.system_type ?? "").toUpperCase();
  const note = String(row.note ?? "");
  if (type !== "DEBT" || systemType !== ORDER) return false;
  return note.includes("cod_reserve_shortfall");
};

export const isCodFailOrderReleaseRow = (row: {
  type?: string | null;
  system_type?: string | null;
  note?: string | null;
  display_label_th?: string | null;
}): boolean => {
  if (row.display_label_th === "คืนค่าประกันขนส่ง COD — ปิดรายการ") return true;
  const type = String(row.type ?? "").toUpperCase();
  const systemType = String(row.system_type ?? "").toUpperCase();
  const note = String(row.note ?? "");
  if (type !== "RELEASE" || systemType !== ORDER) return false;
  if (note.includes("เก็บเงินสำเร็จ")) return false;
  return (
    note.includes("เก็บเงินไม่ได้") ||
    note.includes("หลังเก็บเงินไม่ได้") ||
    note.includes("ปิดรายการ") ||
    note.includes("write-off")
  );
};
